import { ChevronLeftIcon, ChevronRightIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { Link } from 'react-router-dom';

import { SectionTitle } from '@/components/common/SectionTitle';
import { fieldLabel, fieldShort, fieldSource } from '@/features/decisions/config/fieldLabels';
import type { FcfContributorRow, PageInfo } from '@/features/decisions/decisions.api.types';
import { fmtInteger, fmtMoneyM, fmtMoneyMWithSign } from '@/features/decisions/utils/fcfFormat';
import { productStockingLocationHref, type PersonaLinkContext } from '@/features/decisions/utils/replenishmentLinks';

interface Props {
  rows: FcfContributorRow[];
  // Case-insensitive SKU substring search (server-side). Controlled by the page.
  search: string;
  onSearchChange: (value: string) => void;
  netFcfImpact: number | null;
  pageInfo: PageInfo | undefined;
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  isLoading: boolean;
  error: Error | null;
  elapsedMs: number | undefined;
  // Carried into the SKU × Site detail link.
  persona: PersonaLinkContext;
}

export function FcfContributorsTable({
  rows,
  search,
  onSearchChange,
  netFcfImpact,
  pageInfo,
  page,
  setPage,
  pageSize,
  isLoading,
  error,
  elapsedMs,
  persona,
}: Props) {
  const total = pageInfo?.total_groups ?? 0;
  const effectivePageSize = pageInfo?.page_size ?? pageSize;
  const totalPages = Math.max(1, Math.ceil(total / effectivePageSize));
  const startIdx = (page - 1) * effectivePageSize + 1;
  const endIdx = Math.min(total, startIdx + rows.length - 1);

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="flex flex-wrap items-start justify-between gap-3 px-4 py-3">
        <div className="min-w-0">
          <SectionTitle>Result Grain</SectionTitle>
          <p className="mt-0.5 text-xs text-gray-500">Top SKU × Site contributors</p>
          <p className="mt-0.5 text-[10px] text-gray-400" title={fieldSource('net_fcf_impact')}>
            {fieldLabel('net_fcf_impact', 'valueOpportunity')}: {fmtMoneyM(netFcfImpact, 2)}
          </p>
        </div>
        <div className="relative w-full sm:w-64">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search SKU…"
            aria-label="Search contributors by SKU"
            className="w-full rounded-md border border-gray-300 bg-white py-1.5 pl-8 pr-8 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          {search.length > 0 && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => onSearchChange('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {isLoading && <div className="px-4 py-6 text-sm text-gray-600">Loading contributors…</div>}
      {error && (
        <div className="m-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          {error.message.includes('no data') ? (
            'No contributor data found for the selected date range and experiments.'
          ) : (
            <>
              <span className="font-medium">/contributors failed:</span> {error.message}
            </>
          )}
        </div>
      )}

      {!isLoading && !error && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-y border-gray-200 bg-white text-xs text-gray-500">
                  <th className="px-4 py-2 text-left font-medium">Rank</th>
                  <th className="px-4 py-2 text-left font-medium" title={fieldSource('sku')}>
                    SKU
                  </th>
                  <th className="px-4 py-2 text-left font-medium" title={fieldSource('site')}>
                    Site
                  </th>
                  <th
                    className="px-4 py-2 text-right font-medium"
                    title={fieldSource('model_revenue_demand_fulfilled')}
                  >
                    {fieldShort('model_revenue_demand_fulfilled', 'valueOpportunity')}
                    <div className="text-[10px] font-normal text-gray-400">Backtest window</div>
                  </th>
                  <th className="px-4 py-2 text-right font-medium" title={fieldSource('hist_demand')}>
                    {fieldShort('hist_demand', 'valueOpportunity')}
                    <div className="text-[10px] font-normal text-gray-400">Sale UOM</div>
                  </th>
                  <th className="px-4 py-2 text-right font-medium" title={fieldSource('fcf_impact')}>
                    {fieldShort('fcf_impact', 'valueOpportunity')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row, i) => {
                  const rank = (page - 1) * effectivePageSize + i + 1;
                  return (
                    <tr key={`${row.sku}-${row.site}`} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-4 py-2.5 text-gray-700">{rank}</td>
                      <td className="whitespace-nowrap px-4 py-2.5">
                        <Link
                          to={productStockingLocationHref({ sku: row.sku, site: row.site }, persona)}
                          className="font-medium text-blue-700 hover:text-blue-900 hover:underline"
                        >
                          {row.sku}
                        </Link>
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-gray-700">{row.site}</td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums text-gray-800">
                        {fmtMoneyM(row.model_revenue_demand_fulfilled, 2)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums text-gray-700">
                        {row.hist_demand == null ? '—' : `${Math.round(row.hist_demand).toLocaleString()} units`}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-right font-semibold tabular-nums text-gray-900">
                        {fmtMoneyMWithSign(row.fcf_impact)}
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-sm text-gray-500">
                      {search.trim() ? `No SKUs match “${search.trim()}”.` : 'No contributors returned.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Right padding clears the floating KAI assistant button (fixed bottom-right, ~76px footprint). */}
          <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50 py-2 pl-4 pr-20 text-xs text-gray-500">
            <span>
              Showing{' '}
              <span className="font-medium text-gray-700">
                {startIdx}–{endIdx}
              </span>{' '}
              of <span className="font-medium text-gray-700">{fmtInteger(total)}</span> · page {page} of {totalPages}
              {elapsedMs != null && (
                <>
                  {' '}
                  ·{' '}
                  <span className="font-mono text-gray-600" title="Client-side roundtrip (fetch + parse).">
                    {elapsedMs} ms
                  </span>
                </>
              )}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                aria-label="Previous page"
                className="rounded border border-gray-300 bg-white p-0.5 text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-300"
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setPage(page + 1)}
                disabled={page >= totalPages}
                aria-label="Next page"
                className="rounded border border-gray-300 bg-white p-0.5 text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-300"
              >
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
